import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { FiUsers, FiDollarSign, FiMessageSquare, FiFileText, FiMail, FiTrendingUp } from 'react-icons/fi';

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const { data } = await api.get('/admin/dashboard');
      setData(data);
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner text="Loading dashboard..." />;
  if (!data) return <p style={{ color: 'var(--text-secondary)' }}>Failed to load dashboard</p>;

  const { overview } = data;

  const statCards = [
    { label: 'Total Users', value: overview.totalUsers, icon: <FiUsers size={22} />, color: '#3b82f6' },
    { label: 'Paid Users', value: overview.paidUsers, icon: <FiDollarSign size={22} />, color: '#22c55e' },
    { label: 'Credits Used', value: (overview.totalCreditsUsed || 0).toLocaleString(), icon: <FiTrendingUp size={22} />, color: '#f59e0b' },
    { label: 'Credits Remaining', value: (overview.totalCreditsRemaining || 0).toLocaleString(), icon: <FiDollarSign size={22} />, color: '#8b5cf6' },
    { label: 'Total Chats', value: overview.totalChats, icon: <FiMessageSquare size={22} />, color: '#06b6d4' },
    { label: 'Published Blogs', value: overview.publishedBlogs, icon: <FiFileText size={22} />, color: '#ec4899' },
    { label: 'Email Subscribers', value: overview.activeEmailSubs, icon: <FiMail size={22} />, color: '#f97316' },
    { label: 'New This Week', value: overview.newUsersThisWeek, icon: <FiUsers size={22} />, color: '#14b8a6' }
  ];

  return (
    <div>
      <h1 style={styles.title}>Dashboard</h1>

      <div style={styles.statsGrid}>
        {statCards.map((card, i) => (
          <div key={i} style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: card.color + '22', color: card.color }}>
              {card.icon}
            </div>
            <div>
              <div style={styles.statValue}>{card.value}</div>
              <div style={styles.statLabel}>{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Credit Stats */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Credit Usage</h2>
        <div style={styles.planBars}>
          {[
            { label: 'Total Purchased', value: (overview.totalCreditsPurchased || 0).toLocaleString(), color: '#22c55e' },
            { label: 'Total Used', value: (overview.totalCreditsUsed || 0).toLocaleString(), color: '#3b82f6' },
            { label: 'Remaining (all users)', value: (overview.totalCreditsRemaining || 0).toLocaleString(), color: '#8b5cf6' }
          ].map((item, i) => {
            const total = overview.totalCreditsPurchased + (overview.totalUsers * 1000);
            const pct = total > 0 ? Math.round(((item.label === 'Total Used' ? overview.totalCreditsUsed : item.label === 'Remaining (all users)' ? overview.totalCreditsRemaining : overview.totalCreditsPurchased) / total) * 100) : 0;
            return (
              <div key={i} style={styles.planBar}>
                <div style={styles.planBarLabel}>
                  <span style={{ fontWeight: 600 }}>{item.label}</span>
                  <span>{item.value}</span>
                </div>
                <div style={styles.barTrack}>
                  <div style={{ ...styles.barFill, width: `${Math.min(pct, 100)}%`, background: item.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Users */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Recent Users</h2>
        <div style={styles.table}>
          <div style={styles.tableHeader}>
            <span style={{ flex: 2 }}>Name</span>
            <span style={{ flex: 2 }}>Email</span>
            <span style={{ flex: 1 }}>Credits</span>
            <span style={{ flex: 1 }}>Joined</span>
          </div>
          {data.recentUsers.map(u => (
            <div key={u._id} style={styles.tableRow}>
              <span style={{ flex: 2, fontWeight: 500 }}>{u.name}</span>
              <span style={{ flex: 2, color: 'var(--text-muted)' }}>{u.email}</span>
              <span style={{ flex: 1 }}>
                <span style={{ ...styles.badge, background: (u.totalCreditsPurchased || 0) > 0 ? 'var(--accent-dim)' : 'var(--bg-tertiary)', color: (u.totalCreditsPurchased || 0) > 0 ? 'var(--accent)' : 'var(--text-muted)' }}>
                  {(u.credits || 0).toLocaleString()}
                </span>
              </span>
              <span style={{ flex: 1, color: 'var(--text-muted)', fontSize: 13 }}>
                {new Date(u.createdAt).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  title: { fontSize: 28, fontWeight: 700, marginBottom: 24, color: 'var(--text-primary)' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 },
  statCard: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, display: 'flex', alignItems: 'center', gap: 16 },
  statIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  statValue: { fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' },
  statLabel: { fontSize: 13, color: 'var(--text-muted)' },
  section: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 24 },
  sectionTitle: { fontSize: 18, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' },
  planBars: { display: 'flex', flexDirection: 'column', gap: 12 },
  planBar: {},
  planBarLabel: { display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14, color: 'var(--text-secondary)' },
  barTrack: { height: 8, background: 'var(--bg-primary)', borderRadius: 4, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 4, transition: 'width 0.5s' },
  table: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  tableHeader: { display: 'flex', padding: '10px 16px', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: 0.5 },
  tableRow: { display: 'flex', padding: '12px 16px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', alignItems: 'center' },
  badge: { padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }
};
